import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import EmptyState from 'components/ui/emptyState'
import * as api from './api.client'
import {
  AccessRequest,
  AccessRequestStatus,
  UpdateAccessRequestStatus,
} from './types'
import { useHospitalAdmin } from '../adminHospital/hospitalAdminContext'
import ManagedHospitalSelect from '../adminHospital/components/managedHospitalSelect'
import AccessRequestsTable, {
  AccessRequestAction,
} from './components/accessRequestsTable'

const AccessRequestPage: FunctionComponent = () => {
  const { selectedHospital, requiresHospitalSelection } = useHospitalAdmin()
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
  const [updatingAccessRequest, setUpdatingAccessRequest] = useState<{
    id: number
    action: AccessRequestAction
  } | null>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  const loadAccessRequests = useCallback(async () => {
    if (!selectedHospital) {
      setAccessRequests([])
      return
    }

    const accessRequests = await api.getAccessRequests(
      selectedHospital.hospitalId
    )
    setAccessRequests(
      accessRequests.filter(
        (a) =>
          AccessRequestStatus[a.status] ===
          AccessRequestStatus.REQUESTED.toString()
      )
    )
  }, [selectedHospital])

  useEffect(() => {
    loadAccessRequests()
  }, [loadAccessRequests])

  const updateAccessRequest = async (
    accessRequestId: number,
    action: AccessRequestAction
  ) => {
    if (!selectedHospital) {
      return
    }

    setUpdatingAccessRequest({ id: accessRequestId, action })

    await api.updateAccessRequest(
      selectedHospital.hospitalId,
      accessRequestId,
      action === 'approve'
        ? UpdateAccessRequestStatus.APPROVED
        : UpdateAccessRequestStatus.DENIED
    )

    setShowSuccessMessage(true)
    setUpdatingAccessRequest(null)
    loadAccessRequests()
  }

  return (
    <Page fill>
      <PageHeader
        title="Solicitações de acesso"
        subtitle="Médicos que pediram para acompanhar os repasses deste hospital."
        actions={!requiresHospitalSelection && <ManagedHospitalSelect />}
      />

      {showSuccessMessage && (
        <Alert
          variant="success"
          onClose={() => setShowSuccessMessage(false)}
          dismissible
        >
          Solicitação aprovada com sucesso!
        </Alert>
      )}

      {requiresHospitalSelection ? (
        <EmptyState
          icon={<Icon name="hospital" size={20} />}
          title="Nenhum hospital selecionado"
          description="Selecione o hospital para visualizar as solicitações de acesso."
          action={<ManagedHospitalSelect />}
        />
      ) : !accessRequests.length ? (
        <EmptyState icon={<Icon name="inbox" size={20} />} title="Nenhuma solicitação pendente" />
      ) : (
        <AccessRequestsTable
          accessRequests={accessRequests}
          onApprove={(id) => updateAccessRequest(id, 'approve')}
          onDeny={(id) => updateAccessRequest(id, 'deny')}
          updating={updatingAccessRequest}
        />
      )}
    </Page>
  )
}

export default AccessRequestPage
